import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Clock, Wallet, User, ClipboardList, LogOut, Menu, X } from "lucide-react";

import MobileDashboard from "@/mobile/pages/MobileDashboard";
import MobilePayslips from "@/mobile/pages/MobilePayslips";
import MobileClockIn from "@/mobile/pages/MobileClockIn";
import MobileMyTime from "@/mobile/pages/MobileMyTime";
import MobileProfile from "@/mobile/pages/MobileProfile";
import { useAuth } from "@/lib/auth";
import { canSee, moduleEnabledForNav } from "@/lib/perms";
import { NAV } from "@/components/app/navConfig";
import CompanySwitcher from "@/components/app/CompanySwitcher";

// "My Workspace" items act on the signed-in user's own EMPLOYEE record. An
// account with no employee linked (a pure super_admin, which is what a fresh
// deployment ships with) has nothing for them to act on: /me/leaves,
// /me/overtime and /me/payslips all 404 and clock-status 422s. Hide them
// rather than showing a section that cannot work.
const SELF_SERVICE_KEYS = new Set([
    "my_dashboard", "my_profile", "clock_in", "my_time",
    "my_payslips", "my_learning", "my_requests", "my_tickets",
]);
const hasEmployeeProfile = (u) => Boolean(u && (u.employee_id || u.employeeId));
// Some entries share a permission key with a sibling that does NOT need an
// employee record (/rewards works for an admin, /rewards/exchange does not and
// errors with "Your account has no employee record."), so those are matched by
// path rather than key.
const SELF_SERVICE_PATHS = new Set(["/rewards/exchange"]);

const bottomNav = [
  { to: "/me", label: "Home", icon: Home },
  { to: "/me/clock-in", label: "Clock", icon: Clock },
  { to: "/me/time", label: "Time", icon: ClipboardList },
  { to: "/me/payslips", label: "Pay", icon: Wallet },
  { to: "/me/profile", label: "Profile", icon: User },
];

// Routes with a purpose-built mobile screen; everything else renders the real
// routed page (Outlet) so all permitted modules work on mobile too.
function MobileRouteSwitch() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/me/clock-in")) return <MobileClockIn />;
  if (pathname.startsWith("/me/time")) return <MobileMyTime />;
  if (pathname.startsWith("/me/payslips")) return <MobilePayslips />;
  if (pathname.startsWith("/me/profile")) return <MobileProfile />;
  if (pathname === "/me" || pathname === "/") return <MobileDashboard />;
  return <Outlet />;
}

export default function MobileShell() {
  const { user, activeCompanyId, companies, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);

  // Close the drawer on navigation and on Escape.
  useEffect(() => { setDrawer(false); window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDrawer(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const doLogout = () => { logout(); navigate("/login", { replace: true }); };

  const activeCompany = (companies || []).find((c) => c.id === activeCompanyId) || null;
  const sections = NAV.map((sec) => ({
    ...sec,
    items: sec.items.filter((it) => canSee(user, activeCompanyId, it.key) && moduleEnabledForNav(it.key, activeCompany) && ((!SELF_SERVICE_KEYS.has(it.key) && !SELF_SERVICE_PATHS.has(it.to)) || hasEmployeeProfile(user))),
  })).filter((sec) => sec.items.length > 0);

  return (
    <div className="min-h-screen bg-[#f4f8ff]">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-40 h-14 flex items-center gap-2 px-3 border-b border-white/70 bg-white/95 backdrop-blur-xl shadow-sm">
        <button onClick={() => setDrawer(true)} aria-label="Open menu" data-testid="mobile-menu-btn"
          className="h-10 w-10 flex items-center justify-center rounded-xl text-slate-700 hover:bg-slate-100">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 mr-auto">
          <img src="/sahod-mark.png" alt="SAHOD" className="h-7 w-7" />
          <span className="font-black text-slate-800 tracking-tight">SAHOD</span>
        </div>
        <div className="max-w-[52%]"><CompanySwitcher /></div>
      </header>

      {/* Navigation drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDrawer(false)} />
          <nav className="absolute left-0 top-0 bottom-0 w-[82%] max-w-xs bg-white shadow-2xl flex flex-col">
            <div className="h-14 flex items-center gap-2 px-4 border-b">
              <img src="/sahod-mark.png" alt="SAHOD" className="h-7 w-7" />
              <span className="font-black text-slate-800">SAHOD</span>
              <button onClick={() => setDrawer(false)} aria-label="Close menu" className="ml-auto h-9 w-9 flex items-center justify-center rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-3 text-sm">
              {sections.map((sec) => (
                <div key={sec.section} className="mb-3">
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">{sec.section}</div>
                  {sec.items.map((it) => (
                    <NavLink key={it.to} to={it.to} end={it.to === "/me"}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg mt-0.5 ${isActive ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600"}`}>
                      <it.icon className="h-4 w-4" /><span>{it.label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </div>
            <button onClick={doLogout} className="m-3 h-11 flex items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600" data-testid="mobile-logout">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </nav>
        </div>
      )}

      {/* Routed content */}
      <main className="pt-14 pb-20 min-h-screen">
        <div className="px-3 py-3 max-w-full overflow-x-hidden">
          <MobileRouteSwitch />
        </div>
      </main>

      {/* Quick self-service bottom bar. Every tab here targets /me/*, which
          needs an employee record behind the account. An admin-only login got
          five tabs that all dead-end -- the same gap the drawer sections above
          already guard against. The drawer still gives them full navigation. */}
      {hasEmployeeProfile(user) && (
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/70 bg-white/95 shadow-[0_-10px_40px_rgba(15,23,42,0.08)] backdrop-blur-xl">
        <div className="mx-auto grid max-w-md grid-cols-5">
          {bottomNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.to === "/me"}
                className={({ isActive }) => `h-16 flex flex-col items-center justify-center gap-1 text-[11px] transition ${isActive ? "text-blue-700 font-black" : "text-slate-400"}`}>
                {({ isActive }) => (
                  <>
                    <div className={`rounded-2xl px-3 py-1.5 ${isActive ? "bg-blue-50" : ""}`}><Icon className="h-5 w-5" /></div>
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>)}
    </div>
  );
}
